import { useEffect, useRef, type ReactNode } from "react"
import { createPortal } from "react-dom"

/**
 * An overlay that asks (motion.md "Overlays"): a scrim, a centred card, focus on the first
 * field on open and back on the trigger on close, Escape and the scrim close it. Never on
 * load; the page behind is untouched.
 */
export function Overlay({ label, onClose, children, wide }: {
  label: string
  onClose: () => void
  children: ReactNode
  wide?: boolean
}) {
  const card = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null
    const first = card.current?.querySelector<HTMLElement>("input, button, a[href], select, textarea")
    first?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation()
        close.current()
      }
      if (e.key !== "Tab" || !card.current) return
      const items = [...card.current.querySelectorAll<HTMLElement>("input, button, a[href], select, textarea")].filter((el) => !el.hasAttribute("disabled"))
      if (!items.length) return
      const [head, tail] = [items[0], items[items.length - 1]]
      if (e.shiftKey && document.activeElement === head) { e.preventDefault(); tail.focus() }
      else if (!e.shiftKey && document.activeElement === tail) { e.preventDefault(); head.focus() }
    }
    document.addEventListener("keydown", onKey, true)
    return () => {
      document.removeEventListener("keydown", onKey, true)
      back?.focus?.()
    }
  }, [])
  return createPortal(
    <div className="nr-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className={`nr-ov-card${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={label} ref={card}>
        {children}
      </div>
    </div>,
    document.body,
  )
}
